import { Component, OnInit, inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { UrlUtils } from '../../shared/utils/url.utils';
import { EntityService } from '../../core/entity.service';
import { Observable } from 'rxjs';
import { ConfigurationHandlerService } from '../../core/configuration-handler.service';
import { MappingTable } from '../../shared/interfaces/mapping-table.interface';
import { ContentDialogService } from '../../parts/dialogs/content-dialog/content-dialog.service';
import { SnackbarService } from '../../core/snackbar.service';
import { ActivityDetails } from '../../shared/interfaces/activity.interface';
import { ActivitiesService } from '../activities.service';
import { SuccessSnackbarComponent } from '../../parts/snackbars/success-snackbar/success-snackbar.component';
import { animate, style, transition, trigger } from '@angular/animations';
import { Router } from '@angular/router';
import { MultiLanguageService } from '../../parts/multi-language/multi-language.service';
import { FilterListPipe } from '../../shared/pipes/filter-list.pipe';
import { CustomTranslatePipe } from '../../shared/pipes/custom-translate.pipe';
import { TranslateModule } from '@ngx-translate/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { AsyncPipe } from '@angular/common';
import { MatInput } from '@angular/material/input';
import { InputExpanderDirective, FunkyButtonComponent } from '@exl-ng/mulo-common';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';

@Component({
    selector: 'esp-add-activities',
    templateUrl: './add-activities.component.html',
    styleUrls: ['./add-activities.component.scss'],
    animations: [
        trigger('SpinnerIn', [
            transition(':enter', [
                style({
                    opacity: 0,
                    transform: 'translateX(100%) scale(.5)',
                }),
                animate('300ms 300ms cubic-bezier(0.165, 0.84, 0.44, 1)', style({
                    opacity: 1,
                    transform: 'translateX(0) scale(1)',
                })),
            ]),
        ]),
    ],
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatFormField,
        InputExpanderDirective,
        MatLabel,
        MatInput,
        MatError,
        MatSelect,
        MatOption,
        FunkyButtonComponent,
        MatIcon,
        MatProgressSpinner,
        AsyncPipe,
        TranslateModule,
        CustomTranslatePipe,
        FilterListPipe
    ]
})
export class AddActivitiesComponent implements OnInit {
    private formBuilder = inject(UntypedFormBuilder);
    private entityService = inject(EntityService);
    private configurationHandlerService = inject(ConfigurationHandlerService);
    private contentDialogService = inject(ContentDialogService);
    private snackbarService = inject(SnackbarService);
    private activitiesService = inject(ActivitiesService);
    private router = inject(Router);
    private multiLanguageService = inject(MultiLanguageService);

    activityForm: UntypedFormGroup;
    institutionCode = UrlUtils.getParam('institution');

    activityCategories$: Observable<string[]>;
    activityTypes$: Observable<string[]>;
    activityTypesMapping: MappingTable[] = [];

    loading = false;

    ngOnInit() {
        this.initForm();
        this.activityTypesMapping =
            this.configurationHandlerService.getMappingTable(
                'ResearcherActivityTypesMapping'
            ) as MappingTable[];
    }

    initForm() {
        this.activityForm = this.formBuilder.group({
            name: ['', [Validators.required, Validators.maxLength(4000)]],
            categoryCode: ['', Validators.required],
            typeCode: [''],
        });

        this.activityCategories$ = this.entityService.getCodeTableList(
            'ResearcherActivityCategories'
        );
        this.activityTypes$ = this.entityService.getCodeTableList(
            'ResearcherActivityTypes'
        );
    }

    resetTypeSelection(event) {
        this.activityForm.get('typeCode').reset('');
    }

    filterActivityType = (activityCode: string) => {
        return this.activityTypesMapping.some(
            (row) =>
                row.enabled &&
                row.source1 === this.activityForm.value.categoryCode &&
                row.target === activityCode
        );
    };

    submit() {
        this.loading = true;
        const newActivity: ActivityDetails = this.activityForm.value;
        newActivity.name = {
            [this.multiLanguageService.defaultLangKey()]:
                this.activityForm.value?.name,
        };
        newActivity.visibleInPortal = false;
        newActivity.visibleInProfiles = true;
        newActivity.repositoryStatusCode = 'approved';

        this.activitiesService
            .addNewActivity(newActivity)
            .subscribe((result: any) => {
                this.loading = false;
                this.contentDialogService.closeDialog();
                this.navigateToActivityPage(result.url);
                this.showSuccessfullMessage();
            });
    }

    showSuccessfullMessage() {
        this.snackbarService.showComponent(
            SuccessSnackbarComponent,
            'research.add.activity.dialog.success.message',
            9000
        );
    }

    navigateToActivityPage(url) {
        this.router.navigate([url], {
            queryParams: { institution: this.institutionCode, mode: 'edit' },
        });
    }
}
